import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useAo3Theme, type Ao3Theme } from "./ao3UseTheme";

export type Ao3PlatformKey = "android" | "ios" | "harmonyos" | "windows";

export interface Ao3SiteConfig {
  contactEmail: string;
  loginUrl: string;
  downloads: Record<Ao3PlatformKey, string>;
}

const ao3DefaultConfig: Ao3SiteConfig = {
  contactEmail: "txby666666@163.com",
  loginUrl: "",
  downloads: {
    android: "https://ao3app.com/download/android",
    ios: "https://ao3app.com/download/ios",
    harmonyos: "https://ao3app.com/download/harmonyos",
    windows: "https://ao3app.com/download/windows",
  },
};

interface Ao3SiteContextValue {
  config: Ao3SiteConfig;
  theme: Ao3Theme;
  toggleTheme: () => void;
  downloadOpen: boolean;
  openDownload: () => void;
  closeDownload: () => void;
}

const Ao3SiteContext = createContext<Ao3SiteContextValue>({
  config: ao3DefaultConfig,
  theme: "light",
  toggleTheme: () => {},
  downloadOpen: false,
  openDownload: () => {},
  closeDownload: () => {},
});

export function Ao3SiteProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<Ao3SiteConfig>(ao3DefaultConfig);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const { theme, toggleTheme } = useAo3Theme();

  useEffect(() => {
    let active = true;
    // Runtime external config: edit out/ao3-config.json on the server, no rebuild needed.
    const url = `${import.meta.env.BASE_URL}ao3-config.json`;
    fetch(url, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("config unavailable"))))
      .then((data) => {
        if (!active || !data) return;
        setConfig({
          contactEmail: data.contactEmail || ao3DefaultConfig.contactEmail,
          loginUrl: typeof data.loginUrl === "string" ? data.loginUrl : ao3DefaultConfig.loginUrl,
          downloads: { ...ao3DefaultConfig.downloads, ...(data.downloads || {}) },
        });
      })
      .catch(() => {
        /* keep defaults so the site still renders */
      });
    return () => {
      active = false;
    };
  }, []);

  const value: Ao3SiteContextValue = {
    config,
    theme,
    toggleTheme,
    downloadOpen,
    openDownload: () => setDownloadOpen(true),
    closeDownload: () => setDownloadOpen(false),
  };

  return <Ao3SiteContext.Provider value={value}>{children}</Ao3SiteContext.Provider>;
}

export function useAo3Site() {
  return useContext(Ao3SiteContext);
}